import type { ApolloCache, Reference } from '@apollo/client';
import type { ModifierDetails } from '@apollo/client/cache';
import { NetworkStatus, isReference } from '@apollo/client';
import { useCallback, useEffect, useMemo } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useNavigate } from 'react-router-dom';
import { first } from 'lodash-es';
import { useFragment as readFragmentData } from '@/graphql';
import type {
    CreateReportTemplateMutation,
    CreateReportTemplateMutationVariables,
    DeleteReportTemplateMutation,
    DeleteReportTemplateMutationVariables,
    DuplicateReportTemplateMutation,
    DuplicateReportTemplateMutationVariables,
    ReportTemplateEntityType,
    ReportTemplateTypeFieldsFragment,
    ReportTemplatesQuery,
    ReportTemplatesQueryVariables,
    Scalars,
    UpdateReportTemplateMutation,
    UpdateReportTemplateMutationVariables,
} from '@/graphql/types';
import {
    CreateReportTemplateDocument,
    DeleteReportTemplateDocument,
    DuplicateReportTemplateDocument,
    ReportTemplateTypeFieldsFragmentDoc,
    ReportTemplatesDocument,
    UpdateReportTemplateDocument,
} from '@/graphql/types';
import { createAfterCursor, extractNodes } from '../../../utils/relay';
import type { UseOrderAndPaginationControlReturn } from '../../table/hooks';
import { useBoundaryMutation, useBoundaryQuery } from '../../../utils/apollo-client';
import { createPaginatedRefetchOptions } from '../../../utils/paginatedMutation';
import { parseDefinitions } from './elements/renderHelpers';
import type {
    ReportTemplateSettingsSubmitPayload,
    ReportTemplateSettingsValues,
} from './forms/useReportTemplateSettingsFormController';
import { parsePageMargin } from './pageSettings';
import { isReportTemplatePageOrientation, isReportTemplatePageSize } from './typeGuards';
import { canEditReportTemplate } from './reportTemplatePermissions';

export const overviewUrl = '/reports/builder/';

function getDefaultReportTemplateName(): string {
    return gettext('UNTITLED_TEMPLATE');
}

export interface PreviewSelectorConfig {
    description: string;
    inputId: string;
    label: string;
    value: string;
    options: Array<[string, string]>;
}

type ReportTemplatesConnection = NonNullable<ReportTemplatesQuery['reportTemplates']>;
type ReportTemplateEdge = NonNullable<ReportTemplatesConnection['edges'][number]>;

interface ReportTemplatesCacheEdge extends Pick<ReportTemplateEdge, '__typename'> {
    node?: Reference | null;
}

interface ReportTemplatesCacheConnection {
    totalCount?: ReportTemplatesConnection['totalCount'];
    edges?: ReportTemplatesCacheEdge[];
}

type ReportTemplatesFieldContext = ModifierDetails & {
    args?: Pick<ReportTemplatesQueryVariables, 'ids'> | null;
};

interface ReportTemplatesQueryGeneratorResult {
    reportTemplates: ReportTemplateTypeFieldsFragment[];
    loading: boolean;
    updating: boolean;
}

interface ReportTemplateHookResult {
    reportTemplate: ReportTemplateEditValues | null;
    entityType: ReportTemplateEntityType | null;
    update: (
        nextSettings: ReportTemplateSettingsSubmitPayload
    ) => Promise<ReportTemplateSettingsValues>;
    loading: boolean;
    updating: boolean;
}

interface DuplicateReportTemplateHookResult {
    duplicate: (id: Scalars['ID']['input']) => Promise<void>;
    duplicating: boolean;
}

export interface ReportTemplateEditValues extends ReportTemplateSettingsValues {
    entityType: ReportTemplateEntityType;
}

export function createEditUrl(id: string): string {
    return `/reports/builder/edit/${id}`;
}

export type ReportTemplateInUse = NonNullable<
    NonNullable<DeleteReportTemplateMutation['deleteReportTemplate']>['inUse']
>;

function writeReportTemplateToCache<TCacheShape>(
    cache: ApolloCache<TCacheShape>,
    reportTemplate: ReportTemplateTypeFieldsFragment
): Reference | undefined {
    return cache.writeFragment({
        fragment: ReportTemplateTypeFieldsFragmentDoc,
        data: reportTemplate,
    });
}

function modifyReportTemplatesConnections<TCacheShape>(
    cache: ApolloCache<TCacheShape>,
    transformConnection: (
        existingConnection: ReportTemplatesCacheConnection,
        fieldContext: ReportTemplatesFieldContext
    ) => ReportTemplatesCacheConnection | Reference
): void {
    cache.modify({
        fields: {
            reportTemplates(
                existingConnection: ReportTemplatesCacheConnection | Reference | undefined,
                fieldContext: ReportTemplatesFieldContext
            ) {
                if (isReference(existingConnection)) {
                    return existingConnection;
                }

                const normalizedConnection = existingConnection ?? {};
                return transformConnection(normalizedConnection, fieldContext);
            },
        },
    });
}

// Detect template-specific lookup connections.
// Overview/list cache updates should ignore these entries to avoid polluting
// template-include resolve queries after save/delete.
function isTemplateLookupReportTemplatesConnection({
    args,
    storeFieldName,
}: ReportTemplatesFieldContext): boolean {
    const ids = args?.ids;
    if (Array.isArray(ids) && ids.length > 0) {
        return true;
    }

    return storeFieldName.includes('"ids":');
}

export function upsertReportTemplateInConnections<TCacheShape>(
    cache: ApolloCache<TCacheShape>,
    reportTemplate: ReportTemplateTypeFieldsFragment | null | undefined
): void {
    if (!reportTemplate) {
        return;
    }

    const cacheRef = writeReportTemplateToCache(cache, reportTemplate);
    if (!cacheRef) {
        return;
    }

    modifyReportTemplatesConnections(cache, (existingConnection, fieldContext) => {
        const { readField } = fieldContext;

        if (isTemplateLookupReportTemplatesConnection(fieldContext)) {
            return existingConnection;
        }

        const existingEdges = Array.isArray(existingConnection.edges)
            ? existingConnection.edges
            : [];
        const existingIndex = existingEdges.findIndex((edge) => {
            const node = edge?.node;
            return node && readField<string>('id', node) === reportTemplate.id;
        });

        if (existingIndex >= 0) {
            const updatedEdges = [...existingEdges];
            updatedEdges[existingIndex] = {
                ...updatedEdges[existingIndex],
                node: cacheRef,
            };

            return {
                ...existingConnection,
                edges: updatedEdges,
            };
        }

        const edgeTypename =
            existingEdges.find((edge) => typeof edge?.__typename === 'string')?.__typename ??
            'ReportTemplateTypeEdge';
        const existingTotalCount =
            typeof existingConnection.totalCount === 'number'
                ? existingConnection.totalCount
                : existingEdges.length;

        return {
            ...existingConnection,
            totalCount: existingTotalCount + 1,
            edges: [
                {
                    __typename: edgeTypename,
                    node: cacheRef,
                },
                ...existingEdges,
            ],
        };
    });
}

function toReportTemplateFormValues(
    template: ReportTemplateTypeFieldsFragment
): ReportTemplateEditValues {
    const { pageSize, pageOrientation } = template;

    if (!isReportTemplatePageSize(pageSize)) {
        throw new Error(`Invalid persisted pageSize "${String(pageSize)}" for report template.`);
    }

    if (!isReportTemplatePageOrientation(pageOrientation)) {
        throw new Error(
            `Invalid persisted pageOrientation "${String(pageOrientation)}" for report template.`
        );
    }

    const pageMargin = parsePageMargin(template.pageMargin, 'template.pageMargin');

    return {
        id: template.id,
        name: template.name,
        entityType: template.entityType,
        structure: parseDefinitions(JSON.parse(template.structure), ['template.structure']),
        pageSize,
        pageOrientation,
        pageMarginValue: pageMargin.value,
        pageMarginUnit: pageMargin.unit,
    };
}

export function useCreateReportTemplateMutation(): (
    entityType: ReportTemplateEntityType
) => Promise<void> {
    const navigate = useNavigate();
    const [runCreateReportTemplateMutation] = useBoundaryMutation<
        CreateReportTemplateMutation,
        CreateReportTemplateMutationVariables
    >(CreateReportTemplateDocument, {
        update(cache, { data }) {
            const createdTemplate = data?.createReportTemplate?.reportTemplate;
            upsertReportTemplateInConnections(cache, createdTemplate);
        },
    });

    return useCallback(
        async (entityType: ReportTemplateEntityType) => {
            const createResult = await runCreateReportTemplateMutation({
                variables: {
                    entityType,
                    name: getDefaultReportTemplateName(),
                },
            });
            const createdTemplate = readFragmentData(
                ReportTemplateTypeFieldsFragmentDoc,
                createResult.data?.createReportTemplate?.reportTemplate
            );
            if (!createdTemplate) {
                throw new Error('Missing created report template in mutation response.');
            }

            navigate(createEditUrl(createdTemplate.id));
        },
        [navigate, runCreateReportTemplateMutation]
    );
}

export function useDuplicateReportTemplateMutation(): DuplicateReportTemplateHookResult {
    const navigate = useNavigate();
    const [runDuplicateReportTemplateMutation, { loading: duplicating }] = useBoundaryMutation<
        DuplicateReportTemplateMutation,
        DuplicateReportTemplateMutationVariables
    >(DuplicateReportTemplateDocument, {
        update(cache, { data }) {
            const duplicatedTemplate = data?.duplicateReportTemplate?.reportTemplate;
            upsertReportTemplateInConnections(cache, duplicatedTemplate);
        },
    });

    const duplicate = useCallback(
        async (id: Scalars['ID']['input']) => {
            const result = await runDuplicateReportTemplateMutation({ variables: { id } });
            const duplicatedTemplate = readFragmentData(
                ReportTemplateTypeFieldsFragmentDoc,
                result.data?.duplicateReportTemplate?.reportTemplate
            );
            if (!duplicatedTemplate) {
                throw new Error('Missing duplicated report template in mutation response.');
            }

            navigate(createEditUrl(duplicatedTemplate.id));
        },
        [navigate, runDuplicateReportTemplateMutation]
    );

    return { duplicate, duplicating };
}

export function useReportTemplatesQueryGenerator(
    orderAndPaginationControl: UseOrderAndPaginationControlReturn
): ReportTemplatesQueryGeneratorResult {
    const queryVariables = useMemo<ReportTemplatesQueryVariables>(() => {
        const { currentPage, pageSize, orderBy } = orderAndPaginationControl;

        return {
            first: pageSize,
            after: createAfterCursor(currentPage, pageSize),
            orderBy,
        };
    }, [orderAndPaginationControl]);

    const { data, loading, networkStatus } = useBoundaryQuery<
        ReportTemplatesQuery,
        ReportTemplatesQueryVariables
    >(ReportTemplatesDocument, {
        variables: queryVariables,
        fetchPolicy: 'network-only',
    });

    useEffect(() => {
        if (data) {
            orderAndPaginationControl.setTotalCount(data.reportTemplates?.totalCount ?? 0);
        }
    }, [data, orderAndPaginationControl]);

    const reportTemplates = useMemo(
        () =>
            readFragmentData(
                ReportTemplateTypeFieldsFragmentDoc,
                extractNodes(data?.reportTemplates?.edges)
            ),
        [data]
    );

    return {
        reportTemplates,
        loading: loading && networkStatus === NetworkStatus.loading,
        updating: loading && networkStatus === NetworkStatus.setVariables,
    };
}

export function useReportTemplate(id: Scalars['ID']['input']): ReportTemplateHookResult {
    const { showBoundary } = useErrorBoundary();

    const { data, loading, networkStatus } = useBoundaryQuery<
        ReportTemplatesQuery,
        ReportTemplatesQueryVariables
    >(ReportTemplatesDocument, {
        variables: {
            ids: [id],
        },
    });
    const totalCount = data?.reportTemplates?.totalCount;

    useEffect(() => {
        if (typeof totalCount === 'number' && totalCount > 1) {
            showBoundary(new Error('Report template query did not return the expected result.'));
        }
    }, [showBoundary, totalCount]);

    const { reportTemplate, entityType } = useMemo(() => {
        const template = readFragmentData(
            ReportTemplateTypeFieldsFragmentDoc,
            first(data?.reportTemplates?.edges)?.node
        );
        const editableTemplate =
            template && canEditReportTemplate(template.permission) ? template : null;
        return {
            reportTemplate: editableTemplate ? toReportTemplateFormValues(editableTemplate) : null,
            entityType: editableTemplate?.entityType ?? null,
        };
    }, [data]);

    const [updateReportTemplateMutation] = useBoundaryMutation<
        UpdateReportTemplateMutation,
        UpdateReportTemplateMutationVariables
    >(UpdateReportTemplateDocument, {
        update(cache, { data: mutationData }) {
            const updatedTemplate = mutationData?.updateReportTemplate?.reportTemplate;
            upsertReportTemplateInConnections(cache, updatedTemplate);
        },
    });

    const update = useCallback(
        async ({
            name,
            structure,
            pageSize,
            pageOrientation,
            pageMarginValue,
            pageMarginUnit,
        }: ReportTemplateSettingsSubmitPayload) => {
            if (!reportTemplate) {
                throw new Error('Cannot update a missing report template.');
            }

            const result = await updateReportTemplateMutation({
                variables: {
                    id: reportTemplate.id,
                    name,
                    structure: JSON.stringify(structure),
                    pageSize,
                    pageOrientation,
                    pageMargin: { value: pageMarginValue, unit: pageMarginUnit },
                },
            });

            const updatedTemplate = readFragmentData(
                ReportTemplateTypeFieldsFragmentDoc,
                result?.data?.updateReportTemplate?.reportTemplate
            );
            if (!updatedTemplate) {
                throw new Error('Missing updated report template in mutation response.');
            }

            return toReportTemplateFormValues(updatedTemplate);
        },
        [updateReportTemplateMutation, reportTemplate]
    );

    return {
        reportTemplate,
        entityType,
        update,
        loading: loading && networkStatus === NetworkStatus.loading,
        updating: loading && networkStatus === NetworkStatus.setVariables,
    };
}

/**
 * Resolves to null once the template is deleted. Resolves to its uses instead when active daily or
 * weekly reports still use it; nothing changed then.
 */
export function useDeleteReportTemplateMutation(): (
    id: Scalars['ID']['input'],
    force?: boolean
) => Promise<ReportTemplateInUse | null> {
    const { refetchQueries, awaitRefetchQueries } =
        createPaginatedRefetchOptions('ReportTemplates');
    const [deleteReportTemplateMutation] = useBoundaryMutation<
        DeleteReportTemplateMutation,
        DeleteReportTemplateMutationVariables
    >(DeleteReportTemplateDocument, {
        // The overview only changes when the template was deleted.
        refetchQueries: ({ data }) =>
            data?.deleteReportTemplate?.reportTemplate ? refetchQueries : [],
        awaitRefetchQueries,
    });

    return useCallback(
        async (id: Scalars['ID']['input'], force = false) => {
            const result = await deleteReportTemplateMutation({
                variables: { id, force },
            });

            const payload = result?.data?.deleteReportTemplate;
            if (payload?.inUse) {
                return payload.inUse;
            }
            if (!payload?.reportTemplate) {
                throw new Error('Missing deleted report template in mutation response.');
            }

            return null;
        },
        [deleteReportTemplateMutation]
    );
}
